import * as Headless from "@headlessui/react";
import { clsx } from "clsx";
import React from "react";
import { Link, LinkProps } from "./link";
import { Text } from "src/components/base/text";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";

const styles = {
    base: [
        // Base
        "relative isolate inline-flex items-center justify-center gap-x-2 rounded-(--radius-control) border font-semibold",
        // Focus — uses brand-500 token; offset is filled by surface so the ring reads cleanly on cards.
        "focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-(--color-brand-500) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-brand-500)",
        // Disabled
        "data-disabled:opacity-50 data-disabled:cursor-not-allowed",
        // Icon
        "*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]",
    ],
    sizes: {
        sm: "px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(1.5)-1px)] text-sm/5 sm:text-xs/5",
        md: "px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] text-base/6 sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6",
        lg: "px-[calc(--spacing(5)-1px)] py-[calc(--spacing(3)-1px)] text-base/6 sm:px-[calc(--spacing(4)-1px)] sm:py-[calc(--spacing(2.5)-1px)] sm:text-base/6",
    },
    solid: [
        // Optical border, implemented as the button background to avoid corner artifacts
        "border-transparent bg-(--btn-border)",
        // Dark mode: border is rendered on `after` so background is set to button background
        "dark:bg-(--btn-bg)",
        // Button background, implemented as foreground layer to stack on top of pseudo-border layer
        "before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-control)-1px)] before:bg-(--btn-bg)",
        // Drop shadow, applied to the inset `before` layer so it blends with the border
        "before:shadow-sm",
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        "dark:before:hidden",
        // Dark mode: Subtle white outline is applied using a border
        "dark:border-white/5",
        // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
        "after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-control)-1px)]",
        // Inner highlight shadow
        "after:shadow-[shadow:inset_0_1px_--theme(--color-white/15%)]",
        // White overlay on hover
        "data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)",
        // Dark mode: `after` layer expands to cover entire button
        "dark:after:-inset-px dark:after:rounded-(--radius-control)",
        // Disabled
        "data-disabled:before:shadow-none data-disabled:after:shadow-none",
    ],
    outline: [
        // Base
        "border-zinc-950/10 text-zinc-950 data-active:bg-zinc-950/[2.5%] data-hover:bg-zinc-950/[2.5%]",
        // Dark mode
        "dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-active:bg-white/5 dark:data-hover:bg-white/5",
        // Icon
        "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
    ],
    plain: [
        // Base
        "border-transparent text-zinc-950 data-active:bg-zinc-950/5 data-hover:bg-zinc-950/5",
        // Dark mode
        "dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10",
        // Icon
        "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
    ],
    colors: {
        "dark/zinc": [
            "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
            "dark:text-white dark:[--btn-bg:var(--color-zinc-600)] dark:[--btn-hover-overlay:var(--color-white)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
        ],
        light: [
            "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/[2.5%] data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
            "dark:text-white dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
            "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
        ],
        "dark/white": [
            "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
            "dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
        ],
        dark: [
            "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
            "dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
        ],
        white: [
            "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/[2.5%] data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
            "dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-500)] data-hover:[--btn-icon:var(--color-zinc-500)]",
        ],
        zinc: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-zinc-600)] [--btn-border:var(--color-zinc-700)]/90",
            "dark:[--btn-hover-overlay:var(--color-white)]/5",
            "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
        ],
        blue: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-blue-600)] [--btn-border:var(--color-blue-700)]/90",
            "[--btn-icon:var(--color-blue-400)] data-active:[--btn-icon:var(--color-blue-300)] data-hover:[--btn-icon:var(--color-blue-300)]",
        ],
        red: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-600)] [--btn-border:var(--color-red-700)]/90",
            "[--btn-icon:var(--color-red-300)] data-active:[--btn-icon:var(--color-red-200)] data-hover:[--btn-icon:var(--color-red-200)]",
        ],
        amber: [
            "text-amber-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-amber-400)] [--btn-border:var(--color-amber-500)]/80",
            "[--btn-icon:var(--color-amber-600)]",
        ],
        emerald: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-emerald-600)] [--btn-border:var(--color-emerald-700)]/90",
            "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
        ],
        sky: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-sky-500)] [--btn-border:var(--color-sky-600)]/80",
            "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
        ],
        indigo: [
            "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-indigo-500)] [--btn-border:var(--color-indigo-600)]/90",
            "[--btn-icon:var(--color-indigo-300)] data-active:[--btn-icon:var(--color-indigo-200)] data-hover:[--btn-icon:var(--color-indigo-200)]",
        ],
    },
};

type ButtonSize = keyof typeof styles.sizes;

type BaseProps = {
    className?: string;
    children: React.ReactNode;
    size?: ButtonSize;
    loading?: boolean;
};

type ButtonProps = (
    | { color?: keyof typeof styles.colors; outline?: never; plain?: never }
    | { color?: never; outline: true; plain?: never }
    | { color?: never; outline?: never; plain: true }
) &
    BaseProps &
    (
        | Omit<Headless.ButtonProps, "className">
        | Omit<LinkProps, "className" | "render">
        | (Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "href"> & {
              external: true;
              href: string;
          })
    );

function Spinner({ size }: { size: ButtonSize }) {
    const dim = size === "lg" ? "size-5" : size === "sm" ? "size-3.5" : "size-4";
    return (
        <svg data-slot="icon" className={clsx(dim, "animate-spin")} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
            <path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
    );
}

export const Button = React.forwardRef(function Button(
    { color, outline, plain, size = "md", loading, className, children, ...props }: ButtonProps,
    ref: React.ForwardedRef<HTMLElement>,
) {
    const classes = clsx(
        className,
        styles.base,
        styles.sizes[size],
        outline ? styles.outline : plain ? styles.plain : clsx(styles.solid, styles.colors[color ?? "dark/zinc"]),
    );

    const content = (
        <TouchTarget>
            {loading ? <Spinner size={size} /> : null}
            {children}
        </TouchTarget>
    );

    if ("href" in props) {
        if ("external" in props) {
            const { external, ...other } = props;
            return (
                <a {...other} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
                    {content}
                </a>
            );
        } else {
            return (
                <Link
                    preload={"intent"}
                    {...props}
                    className={classes}
                    ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                >
                    {content}
                </Link>
            );
        }
    } else {
        const headlessProps = props as Omit<Headless.ButtonProps, "className">;
        const disabled = loading || (headlessProps as { disabled?: boolean }).disabled;
        return (
            <Headless.Button
                {...headlessProps}
                disabled={disabled}
                className={clsx(classes, "cursor-default")}
                ref={ref}
            >
                {content}
            </Headless.Button>
        );
    }
});

/* Expand the hit area to at least 44×44px on touch devices */
export function TouchTarget({ children }: { children: React.ReactNode }) {
    return (
        <>
            <span
                className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
                aria-hidden="true"
            />
            {children}
        </>
    );
}

type BackButtonProps = ButtonProps & {
    color?: never;
    outline?: never;
    plain?: never;
};

export function BackButton(props: BackButtonProps) {
    const { children, ...other } = props;

    return (
        <Button plain={true} {...other}>
            <ChevronLeftIcon />
            <Text>{children}</Text>
        </Button>
    );
}

type PrimaryButtonProps = ButtonProps & { color?: never; outline?: never; plain?: never };

export function PrimaryButton(props: PrimaryButtonProps) {
    return <Button color={"blue"} {...props} />;
}
